import Minus from "@airmar-emea/Assets/Icons/Minus";
import ProductListActions from "@airmar-emea/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductInformation from "@airmar-emea/Overrides/Widgets/ProductList/ProductListProductInformation";
import { EmeaState } from "@airmar-emea/Store/EmeaState";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    ProductContext,
    ProductContextModel,
    withProduct,
} from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, ProductModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import ProductPrimaryImage from "@insite/content-library/Components/ProductPrimaryImage";
import { ProductDetailsPrimaryImageStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Plus from "@insite/mobius/Icons/Plus";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    excludedSpecifications = "excludedSpecifications",
    showRelatedProducts = "showRelatedProducts",
    relatedProductsTitle = "relatedProductsTitle",
    relatedProductType = "relatedProductType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDocuments]: boolean;
        [fields.showAttributes]: boolean;
        [fields.attributesPosition]: string;
        [fields.documentsPosition]: string;
        [fields.excludedSpecifications]: string;
        [fields.showRelatedProducts]: boolean;
        [fields.relatedProductsTitle]: string;
        [fields.relatedProductType]: string;
    };
}

const carouselType = "relatedProducts";
const seedWithManuallyAssigned = "";
const displayProductsFrom = "allCategories";
const selectedCategoryIds: string[] = [];
const numberOfProductsToDisplay = 10;

const mapStateToProps = (state: EmeaState, widgetProps: WidgetProps) => {
    const relatedProducts = getProductsForProductInfoList(state, widgetProps.id);
    const relatedProductContextList = relatedProducts?.map(
        product =>
        ({
            product,
            productInfo: getProductInfoFromList(state, widgetProps.id, product.id),
        } as ProductContextModel),
    );

    return {
        relatedProducts: getProductsForProductInfoList(state, widgetProps.id),
        productSettings: getSettingsCollection(state).productSettings,
        pageType: getCurrentPage(state).type,
        relatedProductContextList,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    documentTypeText?: TypographyPresentationProps;
    documentTypeIcon?: LazyImageProps;
    documentTypeNameContainer?: InjectableCss;
    cardList?: CardListStyles;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    specificationsAccordionSection: {
        headerProps: {
            css: css`
                button {
                    background: #636262 0% 0% no-repeat padding-box;
                }
            `,
        },
        titleTypographyProps: {
            color: "primary.contrast",
        },
        toggleIconProps: {
            color: "primary.contrast",
            src: Plus,
            size: 26,
        },
        collapseIconProps: {
            color: "primary.contrast",
            src: Minus,
            size: 26,
        },
        expandIconProps: {
            color: "primary.contrast",
            src: Plus,
            size: 26,
        },
    },
    documentTypeText: {
        css: css`
            vertical-align: super;
        `,
    },
    documentLink: {
        typographyProps: {
            css: css`
                display: flex;
            `,
        },
    },
    documentTypeIcon: {
        css: css`
            background-color: ${getColor("common.background")};
            padding-right: 8px;
            & > img {
                object-fit: scale-down;
                height: 100%;
            }
        `,
        height: "60px",
        width: "60px",
    },
    documentTypeNameContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
            css: css``,
            offsetProps: { style: { width: "100%" } },
        },
    },
};

export interface RelatedProductAccordionStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
}

const relatedProductAccordionStyles: RelatedProductAccordionStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            min-width: 320px; /* prevent overlap with second column */
            border-bottom: 1px solid ${getColor("common.border")};
            &:last-child {
                border-bottom: none;
            }
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
};

const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
    },
    primaryImage: {
        centeringWrapper: {
            css: css`
                min-height: 200px;
            `,
        },
        img: {
            css: css`
                width: 100%;
            `,
        },
    },
};

function createAccordionSection(specification: SpecificationModel, expanded: boolean): any {
    return (
        <ManagedAccordionSection
            initialExpanded={expanded}
            key={specification.id.toString()}
            title={specification.nameDisplay}
            data-test-selector="productDetails_specifications_specification"
            {...styles.specificationsAccordionSection}
        >
            {parse(specification.htmlContent, parserOptions)}
        </ManagedAccordionSection>
    );
}

function reorderSpecs(specsToShow: SpecificationModel[] | undefined) {
    const preferredSortOrder = ["Data Output", "Contents", "Logistics"];

    const specifications: SpecificationModel[] = [];
    preferredSortOrder.forEach(spec => {
        const isDisplayName = (s: SpecificationModel) => s.nameDisplay === spec;
        if (specsToShow?.some(isDisplayName)) {
            specifications.push(specsToShow?.filter(isDisplayName)[0]);
        }
    });

    return specifications;
}

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    id,
    product: { brand, attributeTypes, documents, specifications },
    pageType,
    fields,
    productSettings,
    relatedProducts,
    relatedProductContextList,
    loadCarouselProducts,
}) => {
    const productContext = React.useContext(ProductContext);
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";

    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            isBrandDetailsPage,
        });
    }, [fields.relatedProductType, productContext?.product?.id]);

    const renderingDocuments = fields.showDocuments && documents && documents.length > 0;
    const renderingAttributes = fields.showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const renderingRelatedProducts = fields.showRelatedProducts && relatedProducts && relatedProducts.length > 0;

    // AMR-268 - Reorder specification tabs on PDP
    const orderedExcludedSpecifications = `${fields.excludedSpecifications}`;

    const specsToExclude = orderedExcludedSpecifications?.split(",");
    const specsToShow = reorderSpecs(
        specsToExclude != null && specsToExclude.length > 0
            ? specifications?.filter(
                spec => !specsToExclude.some((excludeSpec: string) => excludeSpec === spec.nameDisplay),
            )
            : specifications,
    );

    const getSection = (sectionName: string, specifications?: SpecificationModel[]) =>
        specifications?.filter(
            spec => !specsToExclude.some(s => s === sectionName) && spec.nameDisplay === sectionName,
        )[0];

    const featuresSection = getSection("Features", specifications);
    const prop65Section = getSection("Prop 65", specifications);
    const videosSection = getSection("Videos", specifications);
    const warrantySection = getSection("Warranty", specifications);
    const bottomCoverageCalculatorSection = getSection("Bottom Coverage Calculator", specifications);
    const mapPriceSection = getSection("MAP Price", specifications);

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const documentsAccordion = (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={false}
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {renderingDocuments &&
                    documents &&
                    documents!.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                            >
                                {document.documentType ? (
                                    <LazyImage
                                        {...styles.documentTypeIcon}
                                        src={`${productSettings.documentIconsRootPath}/${document.documentType}.svg`}
                                        errorIconProps={{ ...styles.documentIcon }}
                                    />
                                ) : (
                                    <IconMemo {...styles.documentIcon} />
                                )}
                                <StyledWrapper {...styles.documentTypeNameContainer}>
                                    {document.documentType && (
                                        <Typography {...styles.documentTypeText}>
                                            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                            {translate(document.documentType)}
                                        </Typography>
                                    )}
                                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                                </StyledWrapper>
                            </Link>
                        </GridItem>
                    ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const attributesAccordion = (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={false}
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            {brand && (
                <StyledWrapper {...styles.attributeTypeWrapper}>
                    <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
                    <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                </StyledWrapper>
            )}
            {renderingAttributes &&
                attributeTypes &&
                attributeTypes.map(attributeType => (
                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                        <Typography {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}:&nbsp;
                        </Typography>
                        <Typography {...styles.attributeTypeValuesText}>
                            {attributeType
                                .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </StyledWrapper>
                ))}
        </ManagedAccordionSection>
    );

    const relatedProductAccordion = (
        <ManagedAccordionSection
            title={fields.relatedProductsTitle}
            initialExpanded={true}
            data-test-selector="productDetails_specifications_relatedProducts"
            {...styles.specificationsAccordionSection}
        >
            <CardList extendedStyles={styles.cardList} data-test-selector="productListCardContainerList">
                {relatedProductContextList?.map(productContext => {
                    const product = productContext.product;
                    return (
                        <ProductContext.Provider value={productContext} key={product?.id}>
                            <GridContainer
                                {...relatedProductAccordionStyles.gridContainer}
                                data-test-selector={`productListProductCard${product.id}`}
                            >
                                <GridItem {...relatedProductAccordionStyles.leftColumnGridItem} align="middle">
                                    <ProductImageWithModalView {...product} />
                                </GridItem>
                                <GridItem {...relatedProductAccordionStyles.rightColumnGridItem} align="middle">
                                    <GridContainer {...relatedProductAccordionStyles.rightColumnGridContainer}>
                                        <GridItem {...relatedProductAccordionStyles.infoGridItem}>
                                            <ProductListProductInformation
                                                showBrand={true}
                                                showTitle={true}
                                                showPartNumbers={true}
                                                showAvailability={false} // AMR-342
                                                showAttributes={true}
                                                showReplacementLink={true}
                                            />
                                        </GridItem>
                                        <GridItem {...relatedProductAccordionStyles.actionsGridItem}>
                                            <ProductListActions
                                                showPrice={true}
                                                showAddToList={true}
                                                showAvailability={true}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        </ProductContext.Provider>
                    );
                })}
            </CardList>
        </ManagedAccordionSection>
    );

    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {featuresSection && createAccordionSection(featuresSection, true)}
            {renderingAttributes && attributesAccordion}
            {renderingSpecifications &&
                specsToShow?.map((specification, index) => createAccordionSection(specification, false))}
            {videosSection && createAccordionSection(videosSection, false)}
            {renderingDocuments && documentsAccordion}
            {warrantySection && createAccordionSection(warrantySection, false)}
            {prop65Section && createAccordionSection(prop65Section, false)}
            {bottomCoverageCalculatorSection && createAccordionSection(bottomCoverageCalculatorSection, false)}
            {mapPriceSection && createAccordionSection(mapPriceSection, false)}
            {renderingRelatedProducts && relatedProductAccordion}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsSpecifications)),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
            {
                name: fields.excludedSpecifications,
                displayName: "Excluded Specifications",
                editorTemplate: "TextField",
                defaultValue: "MAP Price,Bottom Coverage Calculator",
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showRelatedProducts,
                displayName: "Show Related Products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.relatedProductsTitle,
                displayName: "Related Products Title",
                editorTemplate: "TextField",
                defaultValue: "Related Products",
                fieldType: "Translatable",
                sortOrder: 7,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 8,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
        ],
    },
};

export default widgetModule;

const ProductImageWithModalView: React.FC<ProductModel> = product => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const image = {
        id: product.id,
        imageAltText: product.imageAltText,
        imageType: "Static",
        largeImagePath: product.largeImagePath,
        mediumImagePath: product.mediumImagePath,
        smallImagePath: product.smallImagePath,
    } as ImageModel;
    return (
        <>
            <ProductPrimaryImage
                product={product}
                image={image}
                onClick={imageClickHandler}
                extendedStyles={primaryImageStyles.primaryImage}
            />
            <Modal
                {...primaryImageStyles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={primaryImageStyles.fullPrimaryImage}
                />
            </Modal>
        </>
    );
};